import {DatasetDataInfoResponse} from "api/model/dataset/DatasetDataInfoResponse";
import DatasetStatusShower from "components/basic/datasetStatusShower/DatasetStatusShower";
import {useComponentStateCallback} from "hooks/dashboard/useComponentStateCallback";
import Plotly, {LayoutAxis} from "plotly.js";
import React, {useEffect, useId, useState} from "react";
import Plot from "react-plotly.js";
import "./DatasetLineChartStyle.scss";
import {useResizeDetector} from "react-resize-detector";
import {APIResponse} from "services/HttpService";
import {calculateYAxisRangeValues} from "utils/math-utils";
import ChartWrapper from "../chart-wrapper/ChartWrapper";

import {copyChartToClipBoard} from "../chartUtils";
import {clipboardIcon, clipIcon, forkIcon, statusIcon} from "../icons";
import {AxesConfig, DatasetChartProps, DatasetSpecification, PlotDataEx} from "../interfaces";

interface IYaxisCLipValues {
    identifier: string;
    yAxisMax?: number;
    yAxisMin?: number;
}

const MAX_DTICKS = 25;

const commonAxisConfig = {
    showspikes: true,
    spikemode: "toaxis",
    automargin: true,
    tickformat: "f",
} as Partial<LayoutAxis>;

const secondaryAxisConfig = {
    ...commonAxisConfig,
    overlaying: "y",
    showgrid: false,
};

const xAxisCommonConfig = {
    spikemode: "toaxis",
    showspikes: true,
};

const layoutConfig = {
    legend: {orientation: "h"},
    margin: {
        l: 0,
        r: 0,
        b: 30,
        t: 20,
    },
} as Plotly.Layout;

const DatasetLineChart = React.memo((props: DatasetChartProps) => {
    const uniqueId = useId();
    const identifiers = props.datasets ? props.datasets.map((ds) => ds.identifier) : props.identifiers;
    const {width, ref} = useResizeDetector();
    const setStatus = useComponentStateCallback(props);

    const [axesGrouped, setAxesGrouped] = useState(props.toggleTracesGrouping ?? true);

    const [dialog, setDialog] = useState(false);

    const [firstData, setFirstData] = useState([]);
    const [dticks, setDticks] = useState<number | undefined>(undefined);
    const [yAxisClipValues, setYAxisClipValues] = useState<Array<IYaxisCLipValues>>([]);
    const [clip, setClip] = useState(false);

    const clipAxis = () => {
        setClip((prevState) => !prevState);
    };

    useEffect(() => {
        let yAxisRangeValues: IYaxisCLipValues[] = [];
        if (clip) {
            yAxisRangeValues = calculateYAxisRangeValues(firstData);
        }
        setYAxisClipValues(yAxisRangeValues);
    }, [clip]);

    const promiseHandler = (res: APIResponse<any>) => {
        setFirstData(res.data);
        return res.data.map((ds: DatasetDataInfoResponse) => {
            return mapQueryData(ds);
        });
    };
    const mapQueryData = (ds: DatasetDataInfoResponse) => {
        const x: Date[] = [];
        const y: number[] = [];

        ds.data.forEach((v) => {
            x.push(new Date(v.key as string));
            y.push(v.value);
        });

        let line = {};
        props.datasets?.map((dataset) => {
            if (dataset.identifier === ds.identifier) {
                line = dataset.line ? dataset.line : {};
            }
        });

        return {
            identifier: ds.identifier,
            name: ds.title,
            type: 'scatter',
            mode: props.chartConf?.mode ? props.chartConf.mode : 'lines',
            marker: props.chartConf?.marker ? {...props.chartConf?.marker} : {},
            unit: ds.unit,
            showlegend: true,
            hovertemplate: `<b>%{y:.3f}</b> at %{x} <br>${ds.title}<extra></extra>`,
            line: line,
            x: x,
            y: y,
        } as PlotDataEx;
    };

    const toggleGroupingHandler = () => {
        setAxesGrouped((axesGrouped) => !axesGrouped);
    };

    const showStatus = () => {
        setDialog(true);
    };

    const groupByUnitTraces = (data: Array<Partial<PlotDataEx>>) => {
        const unitMap = new Map<string, string>();
        const axes: AxesConfig = {};
        const domain = [0, 1];
        for (let i = 0; i < data.length; i++) {
            const unit = data[i].unit!!;
            const trace = data[i];

            const range = getRange(data[i].identifier, props.datasets);
            const yAxisMaxValue = clip ? yAxisClipValues[i]?.yAxisMax : undefined;
            const yAxisMinValue = clip ? yAxisClipValues[i]?.yAxisMin : undefined;

            if (!unitMap.has(unit)) {
                const axCount = unitMap.size + 1;

                if (axCount == 1) {
                    unitMap.set(unit, "y");
                    axes["yaxis"] = createAxisConfig(
                        axCount,
                        unit!,
                        domain,
                        range ? range : undefined,
                        yAxisMaxValue,
                        yAxisMinValue
                    );
                } else {
                    unitMap.set(unit, `y${axCount}`);

                    axes[`yaxis${axCount}`] = createAxisConfig(
                        axCount,
                        unit!,
                        domain,
                        range ? range : undefined,
                        yAxisMaxValue,
                        yAxisMinValue
                    );
                }
            }

            trace.yaxis = unitMap.get(unit)!!;
        }

        axes["xaxis"] = {...xAxisCommonConfig, domain: domain} as LayoutAxis;
        return axes;
    };

    const ungroupTraces = (data: Array<Partial<PlotDataEx>>) => {
        const axes: AxesConfig = {};
        const domain = [0, 1];

        for (let i = 0; i < data.length; i++) {
            const unit = data[i].unit;
            const trace = data[i];
            const axis = i + 1;
            const range = getRange(data[i].identifier, props.datasets);
            let yAxisMaxValue;
            let yAxisMinValue;

            if (yAxisClipValues) {
                yAxisMaxValue = clip ? yAxisClipValues[i]?.yAxisMax : undefined;
                yAxisMinValue = clip ? yAxisClipValues[i]?.yAxisMin : undefined;
            }

            if (axis === 1) {
                axes["yaxis"] = createAxisConfig(
                    axis,
                    unit!,
                    domain,
                    range ? range : undefined,
                    yAxisMaxValue,
                    yAxisMinValue
                );
            } else {
                axes[`yaxis${axis}`] = createAxisConfig(
                    axis,
                    unit!,
                    domain,
                    range ? range : undefined,
                    yAxisMaxValue,
                    yAxisMinValue
                );
                trace.yaxis = `y${axis}`;
            }
        }

        axes["xaxis"] = {...xAxisCommonConfig, domain: domain} as LayoutAxis;

        return axes;
    };

    const createAxisConfig = (
        axis: number,
        unit: string,
        domain: Array<number>,
        range?: Array<number>,
        yAxisMaxValue?: number | undefined,
        yAxisMinValue?: number | undefined
    ): LayoutAxis => {
        if (axis === 1) {
            return {
                title: {
                    text: unit,
                    standoff: 10,
                },
                range: range,
                autorangeoptions: {
                    clipmax: yAxisMaxValue,
                    clipmin: yAxisMinValue,
                },
                ...commonAxisConfig,
            } as LayoutAxis;
        } else if (axis === 2) {
            return {
                title: {
                    text: unit,
                    standoff: 10,
                },
                side: "right",
                anchor: "x",
                range: range,
                autorangeoptions: {
                    clipmax: yAxisMaxValue,
                    clipmin: yAxisMinValue,
                },
                ...secondaryAxisConfig,
            } as LayoutAxis;
        } else {
            let axOffset;
            const pxVal = 100 / width!!;
            const offVal = pxVal * 0.75;

            // Left
            if (axis % 2 == 0) {
                axOffset = ((axis / 2) | 0) * offVal - offVal * 2;
                domain[0] = Math.max(0, axOffset) + offVal;
            }
            // Right
            else {
                axOffset = 1 - ((axis / 2) | 0) * offVal + offVal;

                if (axOffset > 0) {
                    domain[1] = axOffset - offVal - offVal / 3;
                }
            }

            return {
                title: {
                    text: unit,
                    standoff: 4,
                },
                position: axOffset,
                side: "left",
                anchor: "free",
                range: range,
                ...secondaryAxisConfig,
            } as LayoutAxis;
        }
    };

    const getRange = (identifier?: string, datasets?: DatasetSpecification[]) => {
        if (!datasets || !identifier) return undefined;

        return datasets.find((d: DatasetSpecification) => d.identifier === identifier)?.range;
    };

    const chartConfig: Partial<Plotly.Config> = {
        displaylogo: false,
        displayModeBar: props.reportMode ? false : "hover",
        modeBarButtonsToRemove: ["lasso2d", "select2d"],
        modeBarButtonsToAdd: [
            {
                title: "Toggle Y Axis Clip",
                name: "Toggle Y Axis Clip",
                icon: clipIcon,
                click: clipAxis,
            },
            {
                title: "Toggle trace grouping",
                name: "Toggle trace grouping",
                icon: forkIcon,
                click: toggleGroupingHandler,
            },
            {
                title: "Copy chart to Clipboard",
                name: "Copy chart to Clipboard",
                icon: clipboardIcon,
                click: copyChartToClipBoard,
            },
            {
                title: "Show Statistics",
                name: "Show Statistics",
                icon: statusIcon,
                click: showStatus,
            },
        ],
    };
    return (
        <div className="dataset-line-chart" ref={ref} style={{overflow: "hidden", height: `100%`}}>
            <ChartWrapper
                uniqueKey={uniqueId}
                identifiers={identifiers}
                showDateSelection={props.showDateSelection ? props.showDateSelection : true}
                promiseHandler={promiseHandler}
                axesConfig={axesGrouped ? groupByUnitTraces : ungroupTraces}
                chartConfig={chartConfig}
                timeAxisStepSeconds={props.timeAxisStepSeconds}
                maxDTicks={MAX_DTICKS}
                dticks={dticks}
                setDticks={setDticks}
                updateFrequency={props.updateFrequency}
                height={props.height}
                loadConf={props.loadConf}
                hideRefresh={props.hideRefresh}
                dynamicHeight={props.dynamicHeight}
                referenceTime={props.referenceTime}
                chartDialogOpen={props.chartDialogOpen}
                width={props.width}
            >
                {(props) => (
                    <>
                        {dialog && <DatasetStatusShower dialog={dialog} setDialog={setDialog} datasets={firstData}/>}
                        <Plot
                            className="ea-plotly-chart"
                            config={{...props.chartConfig}}
                            data={[...props.data]}
                            layout={{
                                ...props.axesConfig,
                                ...layoutConfig,
                                xaxis: {...props.axesConfig?.xaxis, dtick: dticks},
                                width: props.width,
                                height: props.height,
                            }}
                            onInitialized={() => setStatus({state: "finished"})}
                        />
                    </>
                )}
            </ChartWrapper>
        </div>
    );
});

export default DatasetLineChart;